import { BenefitsDependentEnrollment, BenefitsEnrollment } from "@oysterhr/schema"
import { Button, Modal } from "@oysterhr/ui"
import { useTranslation } from "react-i18next"
import { TransitionToEnrolledForm } from "./TransitionToEnrolledForm"
import { FormValues, useTransitionToEnrolledForm } from "./useTransitionToEnrolledForm"
import { useTransitionToEnrolled } from "../../hooks/useTransitionToEnrolled"
import { useTransitionDependentEnrollmentToEnrolled } from "../../hooks"
import { useConfiguredToasts } from "@oysterhr/feature-components"
import { convertUnitIntoSubunit } from "@oysterhr/common"
import { useMemo } from "react"
import { getEmployerContributionPercentage } from "../../utils/getEmployerContributionPreference"

export interface TransitionToEnrolledModalProps {
  enrollment: BenefitsEnrollment | BenefitsDependentEnrollment
  onClose: () => void
  isOpen: boolean
  parentEnrollmentPriceCurrency?: string
  onSuccess?: () => void
}

export function TransitionToEnrolledModal({
  enrollment,
  onClose,
  isOpen,
  parentEnrollmentPriceCurrency,
  onSuccess,
}: TransitionToEnrolledModalProps) {
  const costSplitAllowed = enrollment.plan.costSplitAllowed
  const [t] = useTranslation("benefits/TransitionToEnrolledModal")
  const { displayErrorToast, displaySuccessToast } = useConfiguredToasts()

  const form = useTransitionToEnrolledForm(enrollment)
  const isDependentEnrollment = enrollment.__typename === "BenefitsDependentEnrollment"

  const handleClose = () => {
    form.reset()
    onClose()
  }

  const handleCompletion = (errors: string[]) => {
    if (errors.length) {
      displayErrorToast({ errors })
      return
    }

    displaySuccessToast({ content: t("enrollmentSuccessful") })
    handleClose()
    onSuccess?.()
  }

  const [transitionDependentEnrollmentToEnrolled, { loading: dependentTransitionLoading }] =
    useTransitionDependentEnrollmentToEnrolled({
      onCompleted: response => {
        handleCompletion(response.transitionDependentEnrollmentToEnrolled.errors)
      },
    })

  const [transitionToEnrolled, { loading }] = useTransitionToEnrolled({
    onCompleted: response => {
      handleCompletion(response.transitionToEnrolled.errors)
    },
  })

  const submit = async ({ coverageStartDate, price, employerContribution }: FormValues) => {
    const currencyCode = price.currency as string
    const employerContributionPayload = costSplitAllowed
      ? {
          employerContribution: {
            currencyCode,
            subunit: convertUnitIntoSubunit(Number(employerContribution), currencyCode),
          },
        }
      : undefined

    const dataPayload = {
      coverageStartDate,
      pricePaidProvider: {
        currencyCode,
        subunit: convertUnitIntoSubunit(Number(price.amount), currencyCode),
      },
      ...employerContributionPayload,
    }

    if (isDependentEnrollment) {
      return transitionDependentEnrollmentToEnrolled({
        variables: {
          input: {
            dependentEnrollmentId: enrollment.id,
            ...dataPayload,
          },
        },
      })
    }

    return transitionToEnrolled({
      variables: {
        input: {
          enrollmentId: enrollment.id,
          ...dataPayload,
        },
      },
    })
  }

  const recommendedPercentage = useMemo(
    () => getEmployerContributionPercentage(enrollment),
    [enrollment],
  )

  return (
    <Modal data-testid="transition-to-enrolled-modal" isOpen={isOpen} onRequestClose={onClose}>
      <Modal.Header>{t("title")}</Modal.Header>
      <Modal.Content>
        <TransitionToEnrolledForm
          parentEnrollmentPriceCurrency={parentEnrollmentPriceCurrency}
          form={form}
          plan={enrollment.plan}
          customerContributionPercentagePreference={recommendedPercentage}
        />
      </Modal.Content>
      <Modal.Footer>
        <Button variation="secondary" onClick={handleClose}>
          {t("cancel")}
        </Button>
        <Button
          onClick={form.handleSubmit(values => submit(values))}
          isLoading={loading || dependentTransitionLoading}
          variation="primary"
        >
          {t("enroll")}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
